/**
 * WithdrawalsSection - Withdrawal management list
 *
 * Uses shared components:
 * - ListLayout (layouts) - page shell
 * - FilterDropdowns/useFilterPanel (core) - filter system
 * - SortButton (composed) - sort selector
 * - MenuButton (composed) - navigation
 * - InfoCard/InfoCardList (composed) - list items
 * - InfoCardDetail (composed) - box-based detail view
 * - BottomSheet (core) - detail container
 *
 * TYPES: Uses Zod contracts from @club-bot/shared/contracts
 * - AdminWithdrawalSummary - list items
 * - AdminWithdrawalDetail - detail view (includes logs)
 *
 * Features:
 * - Search by reference, telegram ID, card number, amount
 * - Filter by status, payment method
 * - Sort by date, amount
 * - Infinite scroll list
 * - Tap to view details
 * - Proof photo viewer
 */

import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import {
  ArrowUpFromLine,
  LayoutDashboard,
  Users,
  ArrowDownToLine,
  FileText,
  Gift,
  Settings,
  Clock,
  TrendingUp,
  TrendingDown,
} from 'lucide-react';
import {
  ListLayout,
  FilterDropdowns,
  useFilterPanel,
  InfoCard,
  InfoCardList,
  InfoCardDetail,
  BottomSheet,
  PopupMessage,
  cardMatchesSearch,
  findSearchHighlights,
} from '@/components/shared';
import type { FilterCategory, DetailBox } from '@/components/shared';
import type { MenuOption } from '@/components/shared/composed/MenuButton';
import type { SortOption } from '@/components/shared/composed/SortButton';
import { useInfiniteScroll } from '@/hooks';

// Import types from shared contracts (Zod schemas)
import type {
  AdminWithdrawalSummary,
  AdminWithdrawalDetail,
  WithdrawalLogEntry,
} from '@club-bot/shared/contracts';
import { resolveMediaUrl } from '@club-bot/shared/contracts';

// Media service URL from environment
const MEDIA_SERVICE_URL = import.meta.env.VITE_MEDIA_SERVICE_URL || 'http://localhost:30003';

// ============================================
// BUILD BOXES FUNCTION
// Uses AdminWithdrawalDetail directly from Zod contracts
// ============================================

interface BuildWithdrawalBoxesOptions {
  /** Withdrawal data from backend (Zod validated) */
  withdrawal: AdminWithdrawalDetail;
  /** Logs from the withdrawal (for edit/resend history) */
  logs: WithdrawalLogEntry[];
  onSendAgainClick?: () => void;
  sendAgainLoading?: boolean;
  onEditFinancialsClick?: () => void;
  editFinancialsLoading?: boolean;
}

/** Build withdrawal detail boxes from Zod-validated data */
function buildWithdrawalBoxes({
  withdrawal,
  logs,
  onSendAgainClick,
  sendAgainLoading,
  onEditFinancialsClick,
  editFinancialsLoading,
}: BuildWithdrawalBoxesOptions): DetailBox[] {
  // Find relevant logs for conditional display
  const editLog = logs.find(l => l.logType === 'edit' || l.logType === 'admin_edit');
  const manualLog = logs.find(l => l.logType === 'manual');
  const resendLog = logs.find(l => l.logType === 'resend');

  // Resolve photo URL using media service
  const photoUrl = resolveMediaUrl(withdrawal.photoUrl, MEDIA_SERVICE_URL);

  return [
    // Box 1: Basic Info (Static)
    {
      id: 'basic-info',
      title: 'Withdrawal Info',
      fields: [
        { label: 'Withdrawal ID', value: withdrawal.referenceNumber },
        { label: 'User ID', value: withdrawal.telegramId },
        { label: 'Original Amount', value: withdrawal.originalAmount, format: 'currency' },
        { label: 'Final Amount', value: withdrawal.amount, format: 'currency' },
        { label: 'Bonus Deducted', value: withdrawal.originalBonus, format: 'currency' },
        { label: 'Card Number', value: withdrawal.cardNumber },
        { label: 'Started', value: withdrawal.createdAt, format: 'date' },
        { label: 'Completed', value: withdrawal.completedAt, format: 'date' },
      ],
      visible: true,
    },

    // Box 2: Conversion Commission (Conditional - only when conversion data exists)
    {
      id: 'conversion-commission',
      title: 'Conversion Info',
      fields: [
        { label: 'USDT Amount', value: withdrawal.usdtAmount },
        { label: 'Exchange Rate', value: withdrawal.exchangeRate },
        { label: 'Commission %', value: withdrawal.commission != null ? `${withdrawal.commission}%` : null },
      ],
      visible: withdrawal.usdtAmount != null || withdrawal.exchangeRate != null,
    },

    // Box 3: Amount Edited (Conditional - from logs)
    {
      id: 'amount-edited',
      title: 'Amount Edited',
      fields: [
        { label: 'New Amount', value: (editLog?.newValues as Record<string, unknown>)?.amount as number | null, format: 'currency' },
        { label: 'Admin', value: editLog?.staffId },
        { label: 'Reason', value: editLog?.note },
        { label: 'Edited At', value: editLog?.createdAt, format: 'date' },
      ],
      visible: !!editLog,
    },

    // Box 4: Manual Completion (Conditional)
    {
      id: 'manual-completion',
      title: 'Manual Completion',
      fields: [
        { label: 'Completed By', value: manualLog?.staffId || withdrawal.assignedTo },
      ],
      image: photoUrl
        ? {
            url: photoUrl,
            label: 'Proof Photo',
          }
        : undefined,
      visible: withdrawal.isManual,
    },

    // Box 5: Automatic Completion (Conditional)
    {
      id: 'automatic-completion',
      title: 'Automatic Completion',
      fields: [
        { label: 'Payment Method', value: withdrawal.paymentMethod },
        { label: 'Transaction ID', value: withdrawal.providerTransactionId },
        { label: 'Provider', value: withdrawal.providerHash },
      ],
      visible: !withdrawal.isManual && withdrawal.status === 'completed',
    },

    // Box 6: Force Resend (Conditional)
    {
      id: 'force-resend-info',
      title: 'Resend History',
      fields: [
        { label: 'Admin', value: resendLog?.staffId },
        { label: 'Reason', value: resendLog?.note },
        { label: 'Resent At', value: resendLog?.createdAt, format: 'date' },
      ],
      visible: withdrawal.resendUsed,
    },

    // Box 7: Actions (Edit Financials + Send Again)
    {
      id: 'actions',
      fields: [],
      actions: [
        ...(onEditFinancialsClick ? [{
          label: 'Edit Financials',
          onClick: onEditFinancialsClick,
          variant: 'secondary' as const,
          loading: editFinancialsLoading,
        }] : []),
        // Only show Send Again if not already used
        ...((onSendAgainClick && !withdrawal.resendUsed) ? [{
          label: 'Send Again',
          onClick: onSendAgainClick,
          variant: 'success' as const,
          loading: sendAgainLoading,
        }] : []),
      ],
      visible: !!onEditFinancialsClick || (!!onSendAgainClick && !withdrawal.resendUsed),
    },
  ];
}

// ============================================
// COMPONENT PROPS
// Uses Zod types from shared contracts
// ============================================

export interface WithdrawalsSectionProps {
  /** List of withdrawals (AdminWithdrawalSummary from Zod) */
  withdrawals: AdminWithdrawalSummary[];
  /** Loading state */
  isLoading?: boolean;
  /** Whether there are more withdrawals to load */
  hasMore?: boolean;
  /** Load more callback */
  onLoadMore?: () => void;
  /** Search callback (for server-side search) */
  onSearch?: (query: string) => void;
  /** Filter callback (for server-side filter) */
  onFilter?: (filters: { status?: string[]; paymentMethod?: string[] }) => void;
  /** Sort callback (for server-side sort) */
  onSort?: (sort: string) => void;
  /** Get full withdrawal details - uses referenceNumber as identifier */
  onGetWithdrawalDetails?: (referenceNumber: string) => Promise<AdminWithdrawalDetail>;
  /** Send again callback (with reason) - uses referenceNumber */
  onSendAgain?: (referenceNumber: string, reason: string) => Promise<void>;
  /** Edit financials callback - uses referenceNumber */
  onEditFinancials?: (referenceNumber: string, data: { usdtAmount?: number; exchangeRate?: number; commission?: number; note?: string }) => Promise<void>;
  /** Navigate to other admin pages */
  onNavigate?: (page: string) => void;
  /** Available payment methods for filter */
  paymentMethods?: string[];
}

// Admin menu options
const adminMenuOptions: MenuOption[] = [
  { value: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { value: 'users', label: 'Users', icon: Users },
  { value: 'deposits', label: 'Deposits', icon: ArrowDownToLine },
  { value: 'withdrawals', label: 'Withdrawals', icon: ArrowUpFromLine },
  { value: 'reports', label: 'Reports', icon: FileText },
  { value: 'bonus', label: 'Bonus', icon: Gift },
  { value: 'cascade', label: 'Cascade', icon: Settings },
  { value: 'referees', label: 'Referees', icon: Users },
];

// Sort options
const sortOptions: SortOption[] = [
  { value: 'newest', label: 'Newest First', icon: Clock },
  { value: 'oldest', label: 'Oldest First', icon: Clock },
  { value: 'amount_high', label: 'Amount (High → Low)', icon: TrendingUp },
  { value: 'amount_low', label: 'Amount (Low → High)', icon: TrendingDown },
];

// Default payment methods
const defaultPaymentMethods = ['fservice_payout', 'nservice_payout', 'eservice_payout'];

// ============================================
// COMPONENT
// ============================================

export function WithdrawalsSection({
  withdrawals,
  isLoading = false,
  hasMore = false,
  onLoadMore,
  onSearch,
  onFilter,
  onSort,
  onGetWithdrawalDetails,
  onSendAgain,
  onEditFinancials,
  onNavigate,
  paymentMethods = defaultPaymentMethods,
}: WithdrawalsSectionProps) {
  // Search state
  const [searchQuery, setSearchQuery] = useState('');

  // Sort state
  const [sortValue, setSortValue] = useState('newest');

  // Scroll container ref for infinite scroll
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Build filter categories dynamically
  const filterCategories: FilterCategory[] = useMemo(
    () => [
      {
        id: 'status',
        label: 'Status',
        options: [
          { value: 'pending', label: 'Pending' },
          { value: 'paid', label: 'Paid' },
          { value: 'completed', label: 'Completed' },
          { value: 'declined', label: 'Declined' },
        ],
      },
      {
        id: 'paymentMethod',
        label: 'Method',
        options: paymentMethods.map((m) => ({ value: m, label: m.toUpperCase() })),
      },
    ],
    [paymentMethods]
  );

  // Filter panel
  const filterPanel = useFilterPanel(filterCategories);

  // Detail sheet state - uses AdminWithdrawalDetail from Zod
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailWithdrawal, setDetailWithdrawal] = useState<AdminWithdrawalDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  // Send Again popup state
  const [sendAgainPopupOpen, setSendAgainPopupOpen] = useState(false);

  // Edit Financials popup state
  const [editFinancialsOpen, setEditFinancialsOpen] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [editUsdtAmount, setEditUsdtAmount] = useState('');
  const [editExchangeRate, setEditExchangeRate] = useState('');
  const [editCommission, setEditCommission] = useState('');
  const [editNote, setEditNote] = useState('');

  // Infinite scroll - pass scroll container ref
  const { sentinelRef } = useInfiniteScroll({
    onLoadMore: onLoadMore || (() => {}),
    hasMore,
    isLoading,
    scrollRef: scrollContainerRef,
  });

  // Notify parent of filter changes
  useEffect(() => {
    onFilter?.({
      status: filterPanel.activeFilters.status,
      paymentMethod: filterPanel.activeFilters.paymentMethod,
    });
  }, [filterPanel.activeFilters, onFilter]);

  // Notify parent of sort changes
  useEffect(() => {
    onSort?.(sortValue);
  }, [sortValue, onSort]);

  // Filter withdrawals by search query (client-side)
  const filteredWithdrawals = useMemo(() => {
    if (!searchQuery) return withdrawals;
    return withdrawals.filter((withdrawal) =>
      cardMatchesSearch(
        {
          referenceNumber: withdrawal.referenceNumber,
          telegramId: withdrawal.telegramId,
          cardNumber: withdrawal.cardNumber,
          amount: String(withdrawal.amount),
        },
        searchQuery
      )
    );
  }, [withdrawals, searchQuery]);

  // Handle search
  const handleSearch = useCallback(
    (query: string) => {
      setSearchQuery(query);
      onSearch?.(query);
    },
    [onSearch]
  );

  // Handle sort
  const handleSort = useCallback((value: string) => {
    setSortValue(value);
  }, []);

  // Handle navigate to detail - uses referenceNumber
  const handleNavigateDetail = useCallback(
    async (referenceNumber: string) => {
      if (!onGetWithdrawalDetails) return;

      setDetailLoading(true);
      setDetailOpen(true);

      try {
        const details = await onGetWithdrawalDetails(referenceNumber);
        setDetailWithdrawal(details);
      } catch (error) {
        console.error('Failed to load withdrawal details:', error);
      } finally {
        setDetailLoading(false);
      }
    },
    [onGetWithdrawalDetails]
  );

  // Open send again popup
  const handleSendAgainClick = useCallback(() => {
    setSendAgainPopupOpen(true);
  }, []);

  // Open edit financials popup
  const handleEditFinancialsClick = useCallback(() => {
    // Pre-populate with current values (using correct field names from Zod)
    if (detailWithdrawal) {
      setEditUsdtAmount(detailWithdrawal.usdtAmount?.toString() ?? '');
      setEditExchangeRate(detailWithdrawal.exchangeRate?.toString() ?? '');
      setEditCommission(detailWithdrawal.commission?.toString() ?? '');
      setEditNote('');
    }
    setEditFinancialsOpen(true);
  }, [detailWithdrawal]);

  // Handle edit financials confirm
  const handleEditFinancialsConfirm = useCallback(async () => {
    if (!detailWithdrawal || !onEditFinancials) return;

    const data: { usdtAmount?: number; exchangeRate?: number; commission?: number; note?: string } = {};
    if (editUsdtAmount) data.usdtAmount = parseFloat(editUsdtAmount);
    if (editExchangeRate) data.exchangeRate = parseFloat(editExchangeRate);
    if (editCommission) data.commission = parseFloat(editCommission);
    if (editNote) data.note = editNote;

    if (Object.keys(data).length === 0 || (!data.usdtAmount && !data.exchangeRate && !data.commission)) {
      return; // Need at least one financial value
    }

    setEditFinancialsOpen(false);
    setEditLoading(true);
    try {
      // Use referenceNumber (string) as identifier
      await onEditFinancials(detailWithdrawal.referenceNumber, data);
      // Refresh detail view
      if (onGetWithdrawalDetails) {
        const updated = await onGetWithdrawalDetails(detailWithdrawal.referenceNumber);
        setDetailWithdrawal(updated);
      }
    } catch (error) {
      console.error('Failed to edit financials:', error);
    } finally {
      setEditLoading(false);
    }
  }, [detailWithdrawal, onEditFinancials, onGetWithdrawalDetails, editUsdtAmount, editExchangeRate, editCommission, editNote]);

  // Handle send again confirmation (with reason from popup)
  const handleSendAgainConfirm = useCallback(async (data: { text?: string }) => {
    if (!detailWithdrawal || !onSendAgain || !data.text) return;

    setSendAgainPopupOpen(false);
    setActionLoading(true);
    try {
      // Use referenceNumber (string) as identifier
      await onSendAgain(detailWithdrawal.referenceNumber, data.text);
      // Refresh detail view
      if (onGetWithdrawalDetails) {
        const updated = await onGetWithdrawalDetails(detailWithdrawal.referenceNumber);
        setDetailWithdrawal(updated);
      }
    } catch (error) {
      console.error('Failed to send again:', error);
    } finally {
      setActionLoading(false);
    }
  }, [detailWithdrawal, onSendAgain, onGetWithdrawalDetails]);

  // Close detail sheet
  const handleCloseDetail = useCallback(() => {
    setDetailOpen(false);
    setDetailWithdrawal(null);
  }, []);

  // Get display label for withdrawal
  const getWithdrawalLabel = (withdrawal: AdminWithdrawalSummary) => {
    return `${withdrawal.amount} ₼`;
  };

  // Get search highlights for withdrawal
  const getWithdrawalHighlights = (withdrawal: AdminWithdrawalSummary): string[] | undefined => {
    if (!searchQuery) return undefined;
    const highlights = findSearchHighlights(
      {
        referenceNumber: withdrawal.referenceNumber,
        telegramId: withdrawal.telegramId,
        cardNumber: withdrawal.cardNumber,
        amount: `${withdrawal.amount} ₼`,
      },
      searchQuery
    );
    return highlights || undefined;
  };

  // Count active filters
  const activeFilterCount = Object.values(filterPanel.activeFilters).reduce(
    (sum, arr) => sum + arr.length,
    0
  );

  return (
    <>
      <ListLayout
        title="Withdrawals"
        count={filteredWithdrawals.length}
        countLabel="withdrawals"
        searchQuery={searchQuery}
        onSearchChange={handleSearch}
        searchPlaceholder="Search withdrawals..."
        onFilterClick={filterPanel.toggleCategoryBox}
        isFilterActive={filterPanel.isCategoryBoxOpen}
        activeFilterCount={activeFilterCount}
        sortOptions={sortOptions}
        sortValue={sortValue}
        onSortChange={handleSort}
        menuOptions={adminMenuOptions}
        menuValue="withdrawals"
        onMenuSelect={(page) => onNavigate?.(page)}
        isLoading={isLoading}
        loadingCount={8}
        isEmpty={filteredWithdrawals.length === 0 && !isLoading}
        scrollContainerRef={scrollContainerRef}
        emptyContent={
          <div className="text-center">
            <ArrowUpFromLine className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            <p className="text-slate-400 text-sm">
              {searchQuery ? 'No withdrawals match your search' : 'No withdrawals found'}
            </p>
          </div>
        }
        filterContent={
          <FilterDropdowns
            categories={filterCategories}
            activeFilters={filterPanel.activeFilters}
            onFiltersChange={filterPanel.setActiveFilters}
            isCategoryBoxOpen={filterPanel.isCategoryBoxOpen}
            onCategoryBoxClose={filterPanel.closeCategoryBox}
            expandedCategories={filterPanel.expandedCategories}
            onExpandCategory={filterPanel.expandCategory}
            onCollapseCategory={filterPanel.collapseCategory}
          />
        }
      >
        <InfoCardList>
          {filteredWithdrawals.map((withdrawal) => (
            <InfoCard
              key={withdrawal.referenceNumber}
              label={getWithdrawalLabel(withdrawal)}
              type="withdrawal"
              searchHighlights={getWithdrawalHighlights(withdrawal)}
              onClick={() => handleNavigateDetail(withdrawal.referenceNumber)}
            />
          ))}
        </InfoCardList>

        {/* Infinite scroll sentinel */}
        <div ref={sentinelRef} className="h-1" />
      </ListLayout>

      {/* Detail Bottom Sheet - Full Screen */}
      <BottomSheet
        isOpen={detailOpen}
        onClose={handleCloseDetail}
        height="full"
      >
        {detailWithdrawal && (
          <InfoCardDetail
            title={detailWithdrawal.referenceNumber}
            subtitle={detailWithdrawal.status.charAt(0).toUpperCase() + detailWithdrawal.status.slice(1)}
            status={detailWithdrawal.status}
            boxes={buildWithdrawalBoxes({
              withdrawal: detailWithdrawal,
              logs: detailWithdrawal.logs || [],
              onSendAgainClick: onSendAgain ? handleSendAgainClick : undefined,
              sendAgainLoading: actionLoading,
              onEditFinancialsClick: onEditFinancials ? handleEditFinancialsClick : undefined,
              editFinancialsLoading: editLoading,
            })}
            onClose={handleCloseDetail}
            isLoading={detailLoading}
          />
        )}
        {detailLoading && !detailWithdrawal && (
          <div className="p-4 space-y-4">
            <div className="h-8 w-2/3 bg-slate-200 rounded-lg animate-pulse" />
            <div className="space-y-3">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="h-12 bg-slate-100 rounded-lg animate-pulse" />
              ))}
            </div>
          </div>
        )}
      </BottomSheet>

      {/* Send Again Confirmation Popup */}
      <PopupMessage
        isOpen={sendAgainPopupOpen}
        title="Send Again"
        message="Are you sure you want to resend this withdrawal? This will trigger a new payout attempt."
        showTextInput
        textPlaceholder="Enter reason for sending again..."
        textRequired
        minTextLength={15}
        proceedLabel="Send Again"
        proceedVariant="success"
        declineLabel="Cancel"
        onDecline={() => setSendAgainPopupOpen(false)}
        onProceed={handleSendAgainConfirm}
        isLoading={actionLoading}
      />

      {/* Edit Financials Popup */}
      <BottomSheet
        isOpen={editFinancialsOpen}
        onClose={() => setEditFinancialsOpen(false)}
        height="auto"
      >
        <div className="p-4 space-y-4">
          <h3 className="text-lg font-semibold text-slate-800">Edit Financials</h3>
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-slate-600 mb-1">USDT Amount</label>
              <input
                type="number"
                step="0.01"
                value={editUsdtAmount}
                onChange={(e) => setEditUsdtAmount(e.target.value)}
                placeholder="Enter USDT amount"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-600 mb-1">Exchange Rate</label>
              <input
                type="number"
                step="0.01"
                value={editExchangeRate}
                onChange={(e) => setEditExchangeRate(e.target.value)}
                placeholder="Enter exchange rate"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-600 mb-1">Commission %</label>
              <input
                type="number"
                step="0.1"
                value={editCommission}
                onChange={(e) => setEditCommission(e.target.value)}
                placeholder="Enter commission %"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-600 mb-1">Note (optional)</label>
              <input
                type="text"
                value={editNote}
                onChange={(e) => setEditNote(e.target.value)}
                placeholder="Enter note"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          <div className="flex gap-3 pt-2">
            <button
              onClick={() => setEditFinancialsOpen(false)}
              className="flex-1 px-4 py-2 border border-slate-300 rounded-lg text-slate-700 font-medium hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              onClick={handleEditFinancialsConfirm}
              disabled={editLoading || (!editUsdtAmount && !editExchangeRate && !editCommission)}
              className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg font-medium hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {editLoading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </BottomSheet>
    </>
  );
}
